import { useEffect, useState } from "react";
import { STATES } from "@/lib/states";
import { useApp } from "@/lib/appContext";
import { useT } from "@/lib/i18n";
import { MapPin, Check, X } from "lucide-react";

export function StatePickerModal() {
  const { state, setState, onboarded, completeOnboarding, lang, setLang } = useApp();
  const [open, setOpen] = useState(!onboarded);
  const [q, setQ] = useState("");
  const t = useT();

  useEffect(() => { if (!onboarded) setOpen(true); }, [onboarded]);

  if (!open) return null;

  const filtered = STATES.filter((s) => {
    const n = (s.name + " " + s.hi).toLowerCase();
    return n.includes(q.toLowerCase());
  });

  const confirm = (code) => { setState(code); completeOnboarding(); setOpen(false); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" role="dialog" data-testid="state-picker-modal">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-xl overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="overline mb-1 text-primary">{t("state")}</div>
              <h2 className="text-xl sm:text-2xl font-black tracking-tight">{t("choose_state")}</h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">{t("choose_state_desc")}</p>
            </div>
            {onboarded && (
              <button data-testid="state-picker-close" aria-label="Close" onClick={()=>setOpen(false)} className="h-8 w-8 rounded-md hover:bg-accent flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input
              data-testid="state-search-input"
              type="text"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Type state name / राज्य का नाम..."
              className="flex-1 h-10 px-3 rounded-md border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="flex rounded-md border border-border bg-background overflow-hidden text-xs" data-testid="lang-toggle-modal">
              <button onClick={()=>setLang("en")} className={`px-2.5 h-10 font-semibold ${lang==="en"?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>EN</button>
              <button onClick={()=>setLang("hi")} className={`px-2.5 h-10 font-semibold ${lang==="hi"?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>हिं</button>
            </div>
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="state-picker-grid">
          {filtered.map((s) => (
            <button
              key={s.code}
              data-testid={`state-opt-${s.code}`}
              onClick={()=>confirm(s.code)}
              className={`px-3 py-2.5 rounded-md text-left border transition flex items-start gap-2 ${state===s.code ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-accent"}`}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-primary/70 shrink-0" strokeWidth={1.75} />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{lang==="hi" ? s.hi : s.name}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.code}</div>
              </div>
              {state===s.code && <Check className="h-3.5 w-3.5 text-primary ml-auto shrink-0" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function StatePill() {
  const { state, setState, lang } = useApp();
  const [open, setOpen] = useState(false);
  const current = STATES.find((s) => s.code === state);
  return (
    <div className="relative">
      <button
        data-testid="state-pill-btn"
        onClick={()=>setOpen(!open)}
        className="h-9 px-2.5 rounded-md border border-border bg-card hover:bg-accent text-xs font-semibold flex items-center gap-1.5 transition"
      >
        <MapPin className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
        <span className="hidden sm:inline">{lang==="hi" ? current?.hi : current?.name}</span>
        <span className="sm:hidden">{current?.code}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 max-h-80 overflow-y-auto rounded-md border border-border bg-popover shadow-lg z-50 p-1" data-testid="state-pill-menu">
          {STATES.map((s) => (
            <button
              key={s.code}
              data-testid={`state-pill-opt-${s.code}`}
              onClick={()=>{ setState(s.code); setOpen(false); }}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded text-left text-xs ${state===s.code ? "bg-primary/10 text-primary font-semibold" : "hover:bg-accent"}`}
            >
              <span className="truncate">{lang==="hi" ? s.hi : s.name}</span>
              <span className="text-[10px] text-muted-foreground">{s.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function LanguagePill() {
  const { lang, setLang } = useApp();
  return (
    <div className="hidden sm:flex rounded-md border border-border bg-card overflow-hidden text-[11px]" data-testid="lang-pill">
      <button data-testid="lang-en-btn" onClick={()=>setLang("en")} className={`h-9 px-2.5 font-semibold ${lang==="en"?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>EN</button>
      <button data-testid="lang-hi-btn" onClick={()=>setLang("hi")} className={`h-9 px-2.5 font-semibold ${lang==="hi"?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>हिं</button>
    </div>
  );
}
